import { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"

import SecondNavbar from "../components/SecondNavbar"
import { getApiHelper, postApiHelper } from "../util/apiHelpers.js"

const ENDPOINT = "fortellis-historical-ingestion"

const errorMessage = (result, fallback) =>
  result?.response?.data?.message || result?.message || fallback

/**
 * Landing page for the approval link emailed to the approver. The token alone decides nothing:
 * the decision is a POST made with the signed-in user's session, and the API rejects anyone who
 * is not the configured approver.
 */
export default function FortellisHistoryApproval() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token") || ""
  const requestId = searchParams.get("requestId") || ""

  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [decision, setDecision] = useState(null)
  const [reason, setReason] = useState("")
  const [rejecting, setRejecting] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const loadPreview = useCallback(async () => {
    if (!token && !requestId) {
      setError("This link is missing its approval token or request id.")
      setLoading(false)
      return
    }
    const result = token
      ? await getApiHelper(
          `${ENDPOINT}/preview?token=${encodeURIComponent(token)}`
        )
      : await getApiHelper(
          `${ENDPOINT}/preview/${encodeURIComponent(requestId)}`
        )
    if (result?.status === 200) {
      setRequest(result.data)
    } else {
      setError(errorMessage(result, "This approval link is no longer valid."))
    }
    setLoading(false)
  }, [token, requestId])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const approve = async () => {
    setSubmitting(true)
    setError("")
    const result = token
      ? await postApiHelper(
          `${ENDPOINT}/approve?token=${encodeURIComponent(token)}`
        )
      : await postApiHelper(
          `${ENDPOINT}/approve/${encodeURIComponent(requestId)}`
        )
    setSubmitting(false)
    if (result?.status === 200) {
      setDecision("Approved")
      setRequest(result.data)
      return
    }
    setError(errorMessage(result, "The approval could not be recorded."))
  }

  const reject = async () => {
    if (reason.trim() === "") {
      setError("A short reason is required to reject the request.")
      return
    }
    setSubmitting(true)
    setError("")
    const result = token
      ? await postApiHelper(
          `${ENDPOINT}/reject?token=${encodeURIComponent(token)}`,
          { reason: reason.trim() }
        )
      : await postApiHelper(
          `${ENDPOINT}/reject/${encodeURIComponent(requestId)}`,
          { reason: reason.trim() }
        )
    setSubmitting(false)
    if (result?.status === 200) {
      setDecision("Rejected")
      setRequest(result.data)
      return
    }
    setError(errorMessage(result, "The rejection could not be recorded."))
  }

  const finished = !loading && (decision || (error && !request))

  return (
    <div className="min-h-screen flex flex-col bg-[#002E5D] px-[40px] pb-[40px] 2xl:text-[14px] text-[12px]">
      <div className="grid w-full h-[145px] text-[#FFFFFF]">
        <SecondNavbar />
      </div>
      <div className="flex justify-center">
        <div className="bg-white rounded-xl w-[640px] max-w-full p-8 shadow-xl">
          <h1 className="font-bold text-[#174a7e] text-[22px] mb-1">
            Historical data ingestion approval
          </h1>
          <p className="text-[13px] text-[#667085] mb-6">
            Approving this starts a one-time historical pull from Fortellis,
            which is billable. Nothing is requested until you approve it.
          </p>

          {loading && <p className="text-[14px] text-[#475467]">Loading…</p>}

          {!loading && decision && (
            <div
              className={`rounded-lg p-4 mb-2 text-[14px] font-semibold ${
                decision === "Approved"
                  ? "bg-[#D1FADF] text-[#027A48]"
                  : "bg-[#F2F4F7] text-[#475467]"
              }`}
            >
              {decision === "Approved"
                ? "Approved. It will run on the next scheduled historical task and the team has been notified."
                : "Recorded as not approved. Nothing was requested from Fortellis and the requester has been notified."}
            </div>
          )}

          {!loading && request && !decision && (
            <>
              <DetailRow label="Dealer" value={request.dealerName} />
              <DetailRow label="Dealer ID" value={request.dealerId} />
              <DetailRow label="Requested by" value={request.requesterEmail} />
              <DetailRow
                label="Data"
                value={`${request.lookbackYears} years of ${request.dataScope}`}
              />
              <DetailRow
                label="Estimated API calls"
                value={`~${request.estimatedApiCalls}`}
              />
              <DetailRow
                label="Estimated cost"
                value={`~$${Number(request.estimatedCostUsd).toFixed(2)} USD`}
              />

              <p className="text-[12px] text-[#667085] mt-4">
                The estimate is based on the standard segment count for this
                date range; the real total can move slightly with data volume.
              </p>

              {rejecting && (
                <div className="mt-5">
                  <label className="block text-[13px] font-semibold text-[#586283] mb-1">
                    Reason for not approving
                  </label>
                  <textarea
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    rows={3}
                    maxLength={1000}
                    className="w-full rounded-lg border border-[#D0D5DD] p-3 text-[13px] focus:outline-[#58628325]"
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 mt-6">
                {!rejecting && (
                  <button
                    type="button"
                    onClick={() => {
                      setError("")
                      setRejecting(true)
                    }}
                    disabled={submitting}
                    className="rounded-lg px-4 py-2 text-[13px] font-semibold border border-[#586283] text-[#586283] hover:bg-[#586283] hover:text-white disabled:opacity-40 transition-colors"
                  >
                    Do not approve
                  </button>
                )}
                {rejecting && (
                  <>
                    <button
                      type="button"
                      onClick={() => setRejecting(false)}
                      disabled={submitting}
                      className="rounded-lg px-4 py-2 text-[13px] font-semibold border border-[#586283] text-[#586283] hover:bg-[#586283] hover:text-white disabled:opacity-40 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={reject}
                      disabled={submitting}
                      className="rounded-lg px-4 py-2 text-[13px] font-semibold text-white bg-[#B42318] hover:bg-[#912018] disabled:opacity-40 transition-colors"
                    >
                      {submitting ? "Saving…" : "Confirm rejection"}
                    </button>
                  </>
                )}
                {!rejecting && (
                  <button
                    type="button"
                    onClick={approve}
                    disabled={submitting}
                    className="rounded-lg px-5 py-2 text-[13px] font-semibold text-white bg-[#298FC2] hover:bg-[#1f7aa8] disabled:opacity-40 transition-colors"
                  >
                    {submitting ? "Saving…" : "Approve this pull"}
                  </button>
                )}
              </div>
            </>
          )}

          {error && (
            <p className="text-[13px] text-red-500 font-medium mt-4">{error}</p>
          )}

          {finished && (
            <div className="flex items-center justify-between gap-4 border-t border-[#F2F4F7] mt-6 pt-4">
              <p className="text-[13px] text-[#475467]">
                Nothing else is needed here. You can close this window.
              </p>
              <div className="flex gap-2 shrink-0">
                <Link
                  to="/fortellis-history/pending"
                  className="rounded-lg px-4 py-2 text-[13px] font-semibold border border-[#586283] text-[#586283] hover:bg-[#586283] hover:text-white transition-colors"
                >
                  Pending list
                </Link>
                <Link
                  to="/"
                  className="rounded-lg px-4 py-2 text-[13px] font-semibold border border-[#298FC2] text-[#298FC2] hover:bg-[#298FC2] hover:text-white transition-colors"
                >
                  Go to VINAmp
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null
  return (
    <div className="flex justify-between border-b border-[#F2F4F7] py-2">
      <span className="text-[13px] text-[#667085]">{label}</span>
      <span className="text-[13px] text-[#1d2939] font-semibold text-right">
        {value}
      </span>
    </div>
  )
}
